// @flow
/* eslint-env browser */
import React, { Component } from 'react';
import classnames from 'classnames';

import { getLoader } from 'Components/utils/icon/icons.loaders';

// TODO consider using an opaque type exported from icons.js
type IconObject = { name: string, loader: Node };
export type IconName = string | null;

export type IconProps = {
  alt?: ?string,
  className?: string,
  classNameInner?: string,
  color?: '' | 'inherit' | Colors,
  hasRtl?: boolean,
  id?: string,
  isNotMonoChrome?: boolean,
  isNotSquare?: boolean,
  name?: IconName,
  rotation?: number,
  style?: *,
  title?: string,
  width?: string,
};

type State = {
  dynamicIcon: IconObject | null,
  isAppMounted: boolean,
  ratio: number,
  isRatioUpdated: boolean,
};

// @todo Avoid the use of the iconName as a string.
// The name of the icon should be received through the enum to be able to migrate from the name of
// the SVG image to an object containing this image name and the template of the SVG image.
const getIconObjectFromName = (iconName: IconName): IconObject | null => {
  if (typeof iconName !== 'string') {
    return null;
  }

  const loader = getLoader(iconName);
  return loader
    ? {
        name: iconName,
        loader,
      }
    : null;
};

const getTransform = ({ hasRtl, rotation }: IconProps, { isAppMounted }: State): string => {
  const rotate = rotation ? `rotate(${rotation}deg)` : '';
  const rtl =
    hasRtl && isAppMounted && document.body && document.body.dir === 'rtl' ? 'scaleX(-1)' : '';

  return [rotate, rtl].join(' ').trim();
};

export class Icon extends Component<IconProps, State> {
  static defaultProps = {
    alt: '',
    color: 'inherit',
    hasRtl: false,
    isNotMonoChrome: false,
    isNotSquare: false,
    name: '',
    rotation: 0,
    width: '1rem',
  };

  node: * = React.createRef();

  constructor(props: IconProps) {
    super(props);

    const { name } = this.props;
    const dynamicIcon = name ? getIconObjectFromName(name) : null;

    this.state = {
      dynamicIcon,
      isAppMounted: false,
      ratio: 100,
      isRatioUpdated: false,
    };
  }

  componentDidMount() {
    if (this.state.dynamicIcon) {
      this.setDimensions();
    }

    this.setState({ isAppMounted: true });
  }

  componentDidUpdate(prevProps: IconProps) {
    if (prevProps.name !== this.props.name) {
      this.setState({
        dynamicIcon: this.props.name ? getIconObjectFromName(this.props.name) : null,
      });
    } else if (this.state.dynamicIcon) {
      this.setDimensions();
    }
  }

  setDimensions = () => {
    if (!this.node || !this.node.current) {
      return;
    }

    const svg = this.node.current.querySelector('svg');

    if (!svg) {
      return;
    }

    if (this.props.isNotSquare && !this.state.isRatioUpdated) {
      try {
        const { width, height } = svg.getBBox();
        const ratio = ((height || 0) / (width || 1)) * 100;

        this.setState(prevState => {
          if (prevState.ratio !== ratio) {
            return { ratio, isRatioUpdated: true };
          }
        });
      } catch (e) {
        // getBBox throw an NS_ERROR_FAILURE exception on Firefox when icon is hidden
      }
    }
  };

  componentWillUnmount() {
    delete this.node;
  }

  render() {
    const {
      alt,
      className,
      classNameInner,
      color = 'inherit',
      hasRtl,
      id,
      isNotMonoChrome,
      isNotSquare,
      name = '',
      rotation,
      style,
      width,
      ...rest
    } = this.props;
    const { dynamicIcon, ratio } = this.state;

    if (!name || !dynamicIcon) {
      return null;
    }

    const styles = {
      ...style,
      width,
      height: isNotSquare ? 'auto' : width,
      transform: getTransform(this.props, this.state),
      transition: 'all ease-out 0.25s',
    };
    const placeholderStyle = { paddingBottom: `${ratio}%` };
    const Renderer = dynamicIcon.loader;
    const optionalProps = {};

    if (id) {
      optionalProps['data-testid'] = id;
    }

    return (
      <span
        {...optionalProps}
        className={classnames(
          className,
          'inline-block overflow-hidden',
          { [`text-${color}`]: color },
          `icon-${dynamicIcon.name}`,
          { 'icon-monochrome': !isNotMonoChrome },
        )}
        ref={this.node}
        style={styles}
        {...(alt ? { title: alt } : null)}
      >
        <span className="relative w-full block" style={placeholderStyle}>
          <Renderer
            {...rest}
            aria-hidden="true"
            focusable="false"
            className={classnames(classNameInner, 'absolute fill-current h-full inset-0 w-full')}
          />
        </span>
      </span>
    );
  }
}
